import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, EnvelopeSimple, User, Key } from '@phosphor-icons/react';

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [betaStatus, setBetaStatus] = useState({ require_beta_code: false, branding: 'Private Beta Access' });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    beta_code: ''
  });

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/beta/status`).then(res => setBetaStatus(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await api.post(endpoint, payload);
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1521114978264-2223e08aa0d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwYXJjaGl0ZWN0dXJhbCUyMGxpbmVzfGVufDB8fHx8MTc4MDA3NzIwNHww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-[#0A1128]/80 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <h1 className="cabinet-grotesk text-4xl font-medium mb-2 text-[#C5A059]">PLANIVANCE AI</h1>
            <p className="text-xs uppercase tracking-[0.16em] text-[#C5A059]/70 mb-6">Client-Facing Financial Planning Platform</p>
            <p className="text-lg text-[#E2E8F0] leading-relaxed">
              Professional financial planning platform to help you track spending, optimize retirement income, and reduce taxes.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFCFB]">
        <div className="w-full max-w-md">
          {betaStatus.require_beta_code && (
            <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A1128] rounded-full">
              <span className="w-2 h-2 bg-[#C5A059] rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">{betaStatus.branding}</span>
            </div>
          )}
          <div className="mb-8">
            <h2 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mb-2">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-[#4A5568]">
              {isLogin ? 'Sign in to your account' : (betaStatus.require_beta_code ? 'Create your account with your beta code' : 'Create your account to begin')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && betaStatus.require_beta_code && (
              <div>
                <Label htmlFor="beta_code" className="text-sm font-medium text-[#1A202C] mb-2 block">
                  Beta Access Code
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C5A059]" size={20} />
                  <Input
                    id="beta_code"
                    type="text"
                    data-testid="auth-beta-code-input"
                    placeholder="PLANIVANCE-BETA-2026"
                    value={formData.beta_code}
                    onChange={(e) => setFormData({ ...formData, beta_code: e.target.value.toUpperCase() })}
                    className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059] font-mono uppercase"
                    required={!isLogin && betaStatus.require_beta_code}
                  />
                </div>
                <p className="text-xs text-[#4A5568] mt-1">Required during private beta</p>
              </div>
            )}

            {!isLogin && (
              <div>
                <Label htmlFor="full_name" className="text-sm font-medium text-[#1A202C] mb-2 block">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                  <Input
                    id="full_name"
                    type="text"
                    data-testid="auth-fullname-input"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#1A202C] mb-2 block">
                Email
              </Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="email"
                  type="email"
                  data-testid="auth-email-input"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-sm font-medium text-[#1A202C]">
                  Password
                </Label>
                {isLogin && (
                  <Link
                    to="/forgot-password"
                    data-testid="auth-forgot-password-link"
                    className="text-xs text-[#0A1128] hover:text-[#C5A059] underline transition-colors"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="password"
                  type="password"
                  data-testid="auth-password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 text-sm font-medium transition-colors"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              data-testid="auth-toggle-button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#4A5568] hover:text-[#0A1128] text-sm transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          <div
            className="mt-6 pt-6 border-t border-slate-200"
            data-testid="auth-privacy-disclosure"
          >
            <p className="text-xs text-[#A0AABF] leading-relaxed">
              <strong className="text-[#4A5568]">Privacy Notice.</strong> During the beta program, PLANIVANCE AI collects basic application usage data to improve the user experience, platform performance, and product development. Personal financial information is handled in accordance with our Privacy Policy. By creating an account you acknowledge this collection and our educational-use-only positioning — PLANIVANCE AI does not provide personalized investment, tax, or legal advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;